import { IRowNode } from "@ag-grid-community/core";
import { GenericTableCell } from "gen/model/genericTableCell";
import { GenericTableRow } from "gen/model/genericTableRow";
import { MoneyTableCellValue } from "gen/model/moneyTableCellValue";
import {
  cloneDeep,
  compact,
  keyBy,
  keys,
  mapValues,
  omitBy,
  pickBy,
  reduce,
  values,
} from "lodash-es";
import { RefObject, useCallback } from "react";
import { AgGridBorderColor } from "shared/components/table/ag/AgGridConstants";
import { DataTableRef } from "shared/components/table/data/DataTable";
import { defaultDataTableRenderCellValue } from "shared/components/table/data/DataTableCellValueRenderer";
import { DataTableColumnContext } from "shared/components/table/data/DataTableContext";
import {
  RenderCellValueFn,
  StylesForCellFn,
} from "shared/components/table/data/DataTableTypes";
import {
  decimalOrZero,
  decimalZero,
  sumDecimalStrings,
} from "shared/utils/DecimalUtils";

// TODO: ENG-8118 better type inference
const FORM_FIELD = "target_amount";
const FORM_FIELD_SUFFIX = `_${FORM_FIELD}`;
const TOTAL_AFTER_ADJUSTMENTS = "total_after_adjustments";
const TOTAL_BEFORE_ADJUSTMENTS = "total_before_adjustments";

export const useAdjustmentsTable = (ref: RefObject<DataTableRef>) => {
  const onCellChange = useCallback(
    (
      _cell: GenericTableCell,
      row: IRowNode<GenericTableRow>,
      columnContext: DataTableColumnContext,
      value: string | null,
    ) => {
      const rowClone = cloneDeep(row.data)!;
      // sync form value
      (
        rowClone.cells[columnContext.columnId].value as MoneyTableCellValue
      ).amount = value;

      const txCodePrefixes = compact(
        keys(rowClone.cells).map((key) => {
          if (key.includes(FORM_FIELD_SUFFIX)) {
            // slice off "target_amount" suffix and return the prefix
            return key.slice(0, -FORM_FIELD_SUFFIX.length);
          }
        }),
      ).map((prefix) => [prefix, `${prefix}${FORM_FIELD_SUFFIX}`] as const);

      // groupedTxValues get you the maybern amount and the adjustment target amonut in a tuple
      const groupedTxFields = keyBy(txCodePrefixes, (val) => val[0]);
      const groupedTxValues = mapValues(groupedTxFields, (cellKeys) =>
        cellKeys.map(
          (key) => (rowClone.cells[key].value as MoneyTableCellValue).amount,
        ),
      );

      const rowSum = reduce(
        groupedTxValues,
        (acc, [originalAmount, adjustment]) =>
          acc.add(decimalOrZero(adjustment || originalAmount || "0")),
        decimalZero(),
      );

      (
        rowClone.cells[TOTAL_AFTER_ADJUSTMENTS].value as MoneyTableCellValue
      ).amount = rowSum.toString();

      // Transaction used to update the cell and the column aggregation total
      ref.current?.tableRef?.api.applyTransaction({
        update: [rowClone],
      });
      ref.current?.tableRef?.api.refreshCells();
    },
    [ref],
  );

  const renderCellValue: RenderCellValueFn = useCallback(
    (props, fieldDisabled, params) =>
      defaultDataTableRenderCellValue(props, fieldDisabled, {
        ...params,
        onCellChange,
      }),
    [onCellChange],
  );

  const onClear = useCallback(() => {
    ref.current?.tableRef?.api.forEachNode(
      (node: IRowNode<GenericTableRow>) => {
        node.setData({
          ...node.data!,
          cells: mapValues(node.data?.cells, (cell, key) => {
            if (
              cell.value?.type === "money" &&
              key === TOTAL_AFTER_ADJUSTMENTS
            ) {
              const onlyOriginalValues = values(
                omitBy(
                  pickBy(
                    node.data?.cells,
                    (cell) => cell.value?.type === "money",
                  ),
                  (_cell, key) =>
                    key.includes(FORM_FIELD) ||
                    key === TOTAL_AFTER_ADJUSTMENTS ||
                    key === TOTAL_BEFORE_ADJUSTMENTS,
                ),
              ).map((cell) => (cell.value as MoneyTableCellValue).amount);

              return {
                ...cell,
                value: {
                  ...cell.value,
                  amount: sumDecimalStrings(onlyOriginalValues).toString(),
                },
              };
            } else {
              return cell;
            }
          }),
        });
      },
    );
  }, [ref]);
  const stylesForCell: StylesForCellFn = useCallback(
    ({ columnContext, type }) => {
      if (
        columnContext.column.id.includes(FORM_FIELD_SUFFIX) &&
        type === "data"
      ) {
        return {
          borderLeft: `1px solid ${AgGridBorderColor}`,
        };
      }
    },
    [],
  );
  return { renderCellValue, onClear, stylesForCell };
};
